import { authorStorageHeaders } from '../../services/authorStorage';
import { displayLabel } from "../../utils/displayLabel";
// AddBook.js
import React, { useMemo, useState, useRef, } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchUserAttributes, getCurrentUser } from "@aws-amplify/auth";
import { Plus, Upload, CheckCircle2, AlertCircle } from "lucide-react";

/* ------------------- Canonical Genre Tree ------------------- */
const GENRE_TREE = {
  Fiction: {
    main: [
      "Children's Books",
      "Fantasy",
      "Romantasy",
      "Science Fiction",
      "Horror",
      "Mystery / Crime",
      "Thriller / Suspense",
      "Romance",
      "Historical Fiction",
      "Western",
      "Literary Fiction",
    ],
    sub: {
      "Children's Books": ["Picture Books", "Middle Grade"],
      Fantasy: ["Dark Fantasy", "High / Epic Fantasy", "Urban Fantasy", "Fairytales & Folklore", "Wizards & Witches"],
      Romantasy: ["Dark Romantasy", "Fae Romantasy", "Paranormal Romantasy", "Shifter Romantasy"],
      "Science Fiction": ["Space Opera", "Dystopian", "Cyberpunk", "Steampunk"],
      Horror: ["Gothic Horror", "Paranormal", "Psychological Horror"],
      "Mystery / Crime": ["Detective", "Cozy Mystery", "Police Procedural"],
      "Thriller / Suspense": ["Spy Thriller", "Political Thriller", "Psychological Thriller"],
      Romance: ["Contemporary Romance", "Historical Romance", "Paranormal Romance"],
      "Historical Fiction": ["War Stories", "Biographical Fiction"],
      Western: ["Classic Western", "Weird West"],
      "Literary Fiction": [],
    },
  },
  "Non-Fiction": {
    main: [
      "Biography / Memoir",
      "History",
      "Self-Help / Personal Development",
      "Religion / Spirituality",
      "Science / Nature",
      "Travel / Adventure",
      "True Crime",
      "Essays",
    ],
    sub: {
      "Biography / Memoir": ["Celebrity Memoir", "Literary Memoir"],
      History: ["Military History", "Cultural History"],
      "Self-Help / Personal Development": ["Habits", "Productivity", "Relationships"],
      "Religion / Spirituality": ["Inspirational", "Comparative Religion"],
      "Science / Nature": ["Popular Science", "Environment"],
      "Travel / Adventure": ["Travelogue", "Exploration"],
      "True Crime": ["Case Study", "Investigative"],
      Essays: [],
    },
  },
};

/* ------------------- Existing config ------------------- */
const formatOptions = ["Paperback", "Hardcover", "Special Edition", "eBook"];

// Endpoints
// Cover-image presign (your existing one that returns author+master URLs)
const COVER_PRESIGN_URL =
  "https://hiuyw6ij1d.execute-api.us-west-2.amazonaws.com/default/generate-presigned-url";

// eBook file presign (new)
const EBOOK_PRESIGN_URL =
  "https://o27abx1bhl.execute-api.us-west-2.amazonaws.com/default/presignEbook";

const SAVE_URL =
  "https://yg2oooqsm0.execute-api.us-west-2.amazonaws.com/default/savebookdata";

const S3_REGION = "us-west-2";

// Build a stable S3 object URL
const objectUrl = (bucket, key) => `https://${bucket}.s3.${S3_REGION}.amazonaws.com/${key.split("/").map(encodeURIComponent).join("/")}`;

/* ------------------- Helpers ------------------- */
const toKeySafe = (value = "") => String(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/['"]/g, '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const dedupeLower = (arr) => {
  const seen = new Set();
  const out = [];
  for (const v of arr || []) {
    const k = String(v).toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(v);
    }
  }
  return out;
};
const clamp = (arr, n) => (arr || []).slice(0, n);

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const createExternalLink = () => ({ label: "", url: "" });
const MAX_VARIATION_IMAGES = 3;
const createDefaultVariation = () => ({
  name: "",
  price: "",
  quantity: "",
  useBaseQuantity: true,
  extrainfo: "",
  images: [],
});
const createDefaultShipping = () => ({
  weight: "",
  length: "",
  width: "",
  height: "",
  mediaMailEligible: false,
  hasExtras: false,
  freeShipping: false,
});

const normalizeShipping = (shipping = {}) => ({
  ...createDefaultShipping(),
  ...(shipping || {}),
  freeShipping: shipping?.freeShipping === true,
});

const shouldShowShipping = (format = {}) =>
  format.type !== "eBook" && !!format.isDirect;

const hasValidDirectShipping = (format = {}) => {
  if (!shouldShowShipping(format)) return true;

  const shipping = normalizeShipping(format.shipping);
  const fields = ["weight", "length", "width", "height"];

  return fields.every((field) => {
    const value = Number(shipping[field]);
    return Number.isFinite(value) && value > 0;
  });
};

const getFormatCoverUrl = (format = {}) => {
  if (format?.coverImageUrl) return format.coverImageUrl;
  const last =
    Array.isArray(format?.coverImages) && format.coverImages.length
      ? format.coverImages[format.coverImages.length - 1]
      : null;

  return last?.url || "";
};

const createDefaultFormat = () => ({
  type: "Paperback",
  extrainfo: "",
  price: "",
  isFree: false,
  quantity: "",
  coverImages: [],
  coverImageUrl: "",
  coverKey: "",
  isDirect: true,
  externalLinks: [],
  ebookFile: null,
  ebookFilename: "",
  ebookFilesize: 0,
  ebookMime: "",
  ebookS3Key: "",
  ebookKey: "",
  ebookUploaded: false,
  shipping: createDefaultShipping(),
  variations: [],
  showVariations: false,
});

const buildSharedImageFormats = (formats = []) => {
  const source =
    formats.find((format) => getFormatCoverUrl(format) || format?.coverKey) || formats[0];

  if (!source) return formats;

  const sharedCoverImageUrl = getFormatCoverUrl(source) || "";
  const sharedCoverKey = source.coverKey || "";
  const sharedCoverImages = Array.isArray(source.coverImages) ? source.coverImages : [];

  return formats.map((format) => ({
    ...format,
    coverImageUrl: sharedCoverImageUrl || format.coverImageUrl || "",
    coverKey: sharedCoverKey || format.coverKey || "",
    coverImages: sharedCoverImages.length ? sharedCoverImages : format.coverImages || [],
  }));
};

const isValidUrl = (value = "") => {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol);
  } catch {
    return false;
  }
};

const normalizeVariationImages = (images) =>
  (Array.isArray(images) ? images : []).slice(0, MAX_VARIATION_IMAGES);

/* ------------------- Component ------------------- */
const AddBook = () => {
  // Core book fields
  const [bookData, setBookData] = useState({
    title: "",
    series: "",
    bookNumber: "",
    tagline: "",
    blurb: "",
    extrainfo: "",
    public: false,
  });
  const hasTitle = !!(bookData.title || "").trim();

  // NEW: series toggle
  const [isSeries, setIsSeries] = useState(false);

  // Genre UI state
  const [category, setCategory] = useState("Fiction");
  const [mainGenres, setMainGenres] = useState([]);
  const [selectedMainForSubs, setSelectedMainForSubs] = useState("");
  const [subgenres, setSubgenres] = useState([]);

  // Formats
  const [formats, setFormats] = useState([createDefaultFormat()]);
  const [useSameImage, setUseSameImage] = useState(false);
  const hasInvalidDirectShipping = useMemo(
    () => formats.some((format) => !hasValidDirectShipping(format)),
    [formats]
  );

  const navigate = useNavigate();

  // Simple per-format upload state for eBooks
  const [ebookUploadingIdx, setEbookUploadingIdx] = useState(null);
  const [busy, setBusy] = useState('');
  const [feedback, setFeedback] = useState('');
  const [canResume, setCanResume] = useState(false);
  const workLock = useRef(false);
  const feedbackRef = useRef(null);
  const showError = message => { setFeedback(message); setTimeout(() => feedbackRef.current?.focus(), 0); };
  const begin = label => { if (workLock.current) return false; workLock.current = true; setBusy(label); setFeedback(''); return true; };
  const end = () => { workLock.current = false; setBusy(''); };
  const resumeSave = async () => {
    if (!begin('Finishing previous book save…')) return;
    try {
      const result = await axios.post(SAVE_URL, { action: 'resume' }, { headers: await authorStorageHeaders() });
      if (result.data?.saved !== true) throw new Error('The save could not be confirmed.');
      setCanResume(false);
      showError('The previous save is complete. Open My Books to view it. Your current entries remain here.');
    } catch (error) { showError(error.response?.data?.error || 'The previous save could not finish. Please retry.'); }
    finally { end(); }
  };


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBookData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  // Handle eBook file selection
  const handleEbookFileChange = (index, file) => {
    if (file && (!/\.(epub|pdf)$/i.test(file.name) || !file.size || file.size > 100 * 1024 * 1024)) return showError('Choose an EPUB or PDF up to 100 MB.');
    setFormats((prev) =>
      prev.map((f, i) =>
        i === index
          ? {
              ...f,
              // store the File object for later upload
              ebookFile: file || null,
              ebookFilename: file ? file.name : "",
              ebookFilesize: file ? file.size : 0,
              ebookMime: file
                ? file.type || (file.name.toLowerCase().endsWith(".epub") ? "application/epub+zip" : "application/pdf")
                : "",
              // reset uploaded marker if they changed file
              ebookS3Key: "",
              ebookKey: "",
              ebookUploaded: false,
              isDirect:
                f.type === "eBook" && !file && !(f.ebookFileUrl || f.ebookS3Key || f.ebookKey || f.ebookUploaded)
                  ? false
                  : f.isDirect,
            }
          : f
      )
    );
  };

  const clearEbookFile = (index) => handleEbookFileChange(index, null);

  const onChangeCategory = (next) => {
    setCategory(next);
    setMainGenres([]);
    setSubgenres([]);
    setSelectedMainForSubs("");
  };

  const availableMain = GENRE_TREE[category].main;
  const subOptions = useMemo(
    () => (selectedMainForSubs ? GENRE_TREE[category].sub[selectedMainForSubs] || [] : []),
    [category, selectedMainForSubs]
  );

  /* ----- Main Genre selection (limit 3) ----- */
  const addMainGenre = (g) => {
    if (!g) return;
    if (mainGenres.includes(g)) {
      setSelectedMainForSubs(g);
      return;
    }
    if (mainGenres.length >= 3) return;
    const next = clamp(dedupeLower([...mainGenres, g]), 3);
    setMainGenres(next);
    setSelectedMainForSubs(g);
  };
  const removeMainGenre = (g) => {
    const next = mainGenres.filter((x) => x !== g);
    setMainGenres(next);
    if (selectedMainForSubs === g) setSelectedMainForSubs(next[0] || "");
  };

  /* ----- Subgenre selection ----- */
  const addSubgenre = (sg) => {
    if (!sg) return;
    setSubgenres(dedupeLower([...(subgenres || []), sg]));
  };
  const removeSubgenre = (sg) => {
    setSubgenres((prev) => (prev || []).filter((x) => x !== sg));
  };

  /* ----- Formats logic ----- */
  const handleFormatChange = (index, field, value) => {
    setFormats((prev) => {
      const next = [...prev];
      const item = { ...next[index] };

      if (field === "type") {
        item.type = value;
        if (value !== "eBook") {
          Object.assign(item, {
            isFree: false,
            ebookFile: null,
            ebookFilename: "",
            ebookFilesize: 0,
            ebookMime: "",
            ebookS3Key: "",
            ebookKey: "",
            ebookUploaded: false,
          });
        }
        if (value === "eBook") {
          const hasEbookFile = !!(item.ebookFileUrl || item.ebookS3Key || item.ebookKey || item.ebookUploaded);
          item.isDirect = hasEbookFile ? item.isDirect : false;
          item.quantity = "";
        }
        next[index] = item;
        return next;
      }

      if (field === "isFree") {
        const isFree = !!value;
        item.isFree = isFree;
        item.price = isFree ? "0.00" : item.price === "0.00" ? "" : item.price ?? "";
        next[index] = item;
        return next;
      }

      if (field === "price") {
        item.price = item.isFree ? "0.00" : value;
        next[index] = item;
        return next;
      }

      if (field === "isDirect") {
        const hasEbookFile = !!(item.ebookFileUrl || item.ebookS3Key || item.ebookKey || item.ebookUploaded);
        item.isDirect = item.type === "eBook" && !hasEbookFile ? false : !!value;
        if (!item.isDirect || item.type === "eBook") {
          item.quantity = "";
          item.showVariations = false;
        }
        next[index] = item;
        return next;
      }

      if (field === "quantity" && item.type === "eBook") {
        item.quantity = "";
        next[index] = item;
        return next;
      }

      if (field.startsWith("shipping.")) {
        const shippingField = field.split(".")[1];
        const shipping = normalizeShipping(item.shipping);
        shipping[shippingField] = value;

        if (shippingField === "hasExtras" && value) {
          shipping.mediaMailEligible = false;
        }
        if (shippingField === "mediaMailEligible" && shipping.hasExtras) {
          shipping.mediaMailEligible = false;
        }

        item.shipping = shipping;
        next[index] = item;
        return next;
      }

      item[field] = value;
      next[index] = item;
      return next;
    });
  };

  const handleExternalLinkChange = (formatIndex, linkIndex, field, value) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? {
              ...format,
              externalLinks: (format.externalLinks || []).map((link, currentLinkIndex) =>
                currentLinkIndex === linkIndex ? { ...link, [field]: value } : link
              ),
            }
          : format
      )
    );
  };

  const addExternalLink = (formatIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? { ...format, externalLinks: [...(format.externalLinks || []), createExternalLink()] }
          : format
      )
    );
  };

  const removeExternalLink = (formatIndex, linkIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? {
              ...format,
              externalLinks: (format.externalLinks || []).filter((_, currentLinkIndex) => currentLinkIndex !== linkIndex),
            }
          : format
      )
    );
  };

  const addFormat = () => {
    setFormats((prev) => [...prev, createDefaultFormat()]);
  };
  const removeFormat = (index) => {
    setFormats((prev) => prev.filter((_, idx) => idx !== index));
  };

  const toggleVariationsSection = (formatIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex ? { ...format, showVariations: !format.showVariations } : format
      )
    );
  };

  const addVariation = (formatIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? { ...format, variations: [...(format.variations || []), createDefaultVariation()], showVariations: true }
          : format
      )
    );
  };

  const removeVariation = (formatIndex, variationIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? {
              ...format,
              variations: (format.variations || []).filter((_, currentIndex) => currentIndex !== variationIndex),
            }
          : format
      )
    );
  };

  const handleVariationChange = (formatIndex, variationIndex, field, value) => {
    setFormats((prev) =>
      prev.map((format, idx) => {
        if (idx !== formatIndex) return format;
        const nextVariations = (format.variations || []).map((variation, currentIndex) => {
          if (currentIndex !== variationIndex) return variation;
          if (field === "useBaseQuantity") {
            return {
              ...variation,
              useBaseQuantity: !!value,
              quantity: value ? "" : variation.quantity,
            };
          }
          return { ...variation, [field]: value };
        });
        return { ...format, variations: nextVariations };
      })
    );
  };

  // PUT a file to a presigned URL; return key
  // Put this inside AddBook component, above handleImageUpload
const putToPresigned = async (file, uploadUrl, contentType) => {
  // Detect if the URL requires the SSE header
  let needsSSE = false;
  try {
    const q = uploadUrl.split("?")[1] || "";
    const params = new URLSearchParams(q);
    const signed = decodeURIComponent(params.get("X-Amz-SignedHeaders") || "");
    needsSSE = signed.toLowerCase().includes("x-amz-server-side-encryption");
  } catch {}

  const headers = { "Content-Type": contentType };
  if (needsSSE) headers["x-amz-server-side-encryption"] = "AES256";

  const r = await fetch(uploadUrl, { method: "PUT", headers, body: file });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`Upload failed: ${r.status} ${text}`);
  }
};


  const uploadCoverImageAsset = async ({ file, formatType }) => {
    if (!bookData.title.trim()) throw new Error('Enter a title before uploading a cover.');
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || !file.size || file.size > 10 * 1024 * 1024) throw new Error('Choose a PNG, JPEG, or WebP up to 10 MB.');
    await getCurrentUser();
    const attributes = await fetchUserAttributes();
    const sub = attributes["sub"];
    const firstName = attributes["custom:firstname"];
    const lastName = attributes["custom:lastname"];

    const presignPayload = {
      sub,
      firstName,
      lastName,
      bookTitle: bookData.title || "Untitled",
      format: formatType || "General",
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type,
      uploadType: "book",
      ...(isSeries && bookData.series?.trim() ? { series: bookData.series.trim() } : {}),
      ...(isSeries && String(bookData.bookNumber || "").trim()
        ? { bookNumber: String(bookData.bookNumber).trim() }
        : {}),
    };

    const { data: ps } = await axios.post(COVER_PRESIGN_URL, presignPayload, { headers: await authorStorageHeaders() });
    if (!ps?.author?.url || !ps?.master?.url) {
      throw new Error("Presign response missing author/master URLs");
    }

    await Promise.all([
      putToPresigned(file, ps.author.url, file.type),
      putToPresigned(file, ps.master.url, file.type),
    ]);

    const coverImageUrl = objectUrl(ps.master.bucket, ps.master.key);
    return {
      coverImageUrl,
      coverKey: ps.master.key,
      imageEntry: {
        authorBucket: ps.author.bucket,
        authorKey: ps.author.key,
        masterBucket: ps.master.bucket,
        masterKey: ps.master.key,
        url: coverImageUrl,
        filename: file.name,
        contentType: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
      },
    };
  };

  /* ----- Cover image upload (existing flow using COVER_PRESIGN_URL) ----- */
  const handleImageUpload = async (index, files) => {
    if (!begin("Uploading cover…")) return;
    try {
      const file = files?.[0];
      if (!file) return;

      const uploadFormat = formats[index]?.type || formats[0]?.type || "General";
      const { coverImageUrl, coverKey, imageEntry } = await uploadCoverImageAsset({
        file,
        formatType: uploadFormat,
      });

      setFormats((prev) => {
        if (useSameImage) {
          return prev.map((format) => ({
            ...format,
            coverImageUrl,
            coverKey,
            coverImages: [...(format.coverImages || []), imageEntry],
          }));
        }

        const updated = [...prev];
        const f = { ...(updated[index] || {}) };
        f.coverImageUrl = coverImageUrl;
        f.coverKey = coverKey;
        f.coverImages = [...(f.coverImages || []), imageEntry];
        updated[index] = f;
        return updated;
      });
    } catch (err) {
      console.error("❌ Upload failed", err);
      showError(`Failed to upload image: ${err?.response?.data?.error || err.message}`);
    } finally { end(); }
  };

  const handleVariationImageUpload = async (formatIndex, variationIndex, files) => {
    if (!begin("Uploading variation image…")) return;
    try {
      const file = files?.[0];
      if (!file) return;

      const format = formats[formatIndex] || {};
      const variation = (format.variations || [])[variationIndex] || {};
      const existing = normalizeVariationImages(variation.images);

      if (existing.length >= MAX_VARIATION_IMAGES) {
        showError(`Variations support up to ${MAX_VARIATION_IMAGES} images.`);
        return;
      }

      const { imageEntry } = await uploadCoverImageAsset({
        file,
        formatType: `${format.type || "Format"}-Variation`,
      });

      setFormats((prev) =>
        prev.map((f, idx) => {
          if (idx !== formatIndex) return f;
          const nextVariations = (f.variations || []).map((v, currentIdx) => {
            if (currentIdx !== variationIndex) return v;
            return {
              ...v,
              images: normalizeVariationImages([...(v.images || []), imageEntry]),
            };
          });
          return { ...f, variations: nextVariations };
        })
      );
    } catch (err) {
      console.error("❌ Variation upload failed", err);
      showError(`Failed to upload variation image: ${err?.response?.data?.error || err.message}`);
    } finally { end(); }
  };

  const removeVariationImage = (formatIndex, variationIndex, imageIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) => {
        if (idx !== formatIndex) return format;
        return {
          ...format,
          variations: (format.variations || []).map((variation, currentVariationIndex) =>
            currentVariationIndex === variationIndex
              ? {
                  ...variation,
                  images: normalizeVariationImages(variation.images).filter(
                    (_, currentImageIndex) => currentImageIndex !== imageIndex
                  ),
                }
              : variation
          ),
        };
      })
    );
  };

  /* ----- eBook upload (new flow using EBOOK_PRESIGN_URL) ----- */
  const uploadEbookForFormat = async (idx) => {
    if (!begin("Uploading eBook…")) return;
    try {
      const f = formats[idx];
      if (!f || f.type !== "eBook") return;

      const title = (bookData.title || "").trim();
      if (!title) return showError("Enter a Title before uploading the eBook.");
      if (!f.ebookFile) return showError("Choose an EPUB or PDF first.");

      setEbookUploadingIdx(idx);

      await getCurrentUser();
      const attrs = await fetchUserAttributes();
      const userSub   = attrs["sub"];
      const firstName = attrs["custom:firstname"];
      const lastName  = attrs["custom:lastname"];

      const titleKey = toKeySafe(title); // << canonical folder key (matches savebookdata)
      const ext  = f.ebookFilename?.toLowerCase().endsWith(".epub") ? "epub" : "pdf";
      const mime = ext === "epub" ? "application/epub+zip" : "application/pdf";

      // Call your presign endpoint with titleKey (NOT bookSlug)
      const { data } = await axios.post(EBOOK_PRESIGN_URL, {
        userSub, first: firstName, last: lastName,
        titleKey, title, ext, mime, size: f.ebookFile.size
      }, { headers: await authorStorageHeaders() });

      if (!data?.uploadUrl || !data?.key) throw new Error("Presign missing uploadUrl/key");

      // PUT the file (your helper already adds the AES256 header if needed)
      await putToPresigned(f.ebookFile, data.uploadUrl, mime);

      // Stash metadata for savebookdata
      setFormats(prev => prev.map((it, i) =>
        i === idx ? {
          ...it,
          ebookS3Key: data.key,
          ebookKey: data.key,
          ebookMime: mime,
          ebookFilesize: f.ebookFilesize,
          ebookFilename: f.ebookFilename,
          ebookUploaded: true
        } : it
      ));
    } catch (err) {
      console.error("❌ eBook upload failed", err);
      showError(`Failed to upload eBook: ${err?.response?.data?.error || err.message}`);
    } finally {
      setEbookUploadingIdx(null); end();
    }
  };


  /* ----- Save (publishNow controls "public") ----- */
  const handleSave = async (publishNow = false) => {
    if (!begin(publishNow ? 'Publishing book…' : 'Saving draft…')) return;
    let requestStarted = false;
    try {
      if (!bookData.title.trim() || !toKeySafe(bookData.title)) throw new Error('Enter a title containing letters or numbers.');
      if (publishNow && (!bookData.blurb.trim() || mainGenres.length === 0)) throw new Error('Add a blurb and at least one genre before publishing.');
      if (new Set(formats.map(f => f.type)).size !== formats.length) throw new Error('Each format must be unique.');
      if (isSeries && (!bookData.series.trim() || !Number.isSafeInteger(Number(bookData.bookNumber)) || Number(bookData.bookNumber) < 1)) throw new Error('Enter a series name and a positive whole book number.');
      await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const sub = attributes["sub"];
      const firstName = attributes["custom:firstname"];
      const lastName = attributes["custom:lastname"];


      const cleanGenres = clamp(dedupeLower(mainGenres), 3);
      const cleanSubgenres = dedupeLower(subgenres);
      const preparedFormats = useSameImage ? buildSharedImageFormats(formats) : formats;

      const cleanedFormats = (preparedFormats || []).map((f) => {
        const externalLinks = (f.externalLinks || [])
          .map((link) => ({
            label: (link.label || "").trim(),
            url: (link.url || "").trim(),
          }))
          .filter((link) => link.label || link.url);

        for (const link of externalLinks) {
          if (!link.label || !link.url) {
            throw new Error(`Each retailer link for ${f.type} must include both a label and URL.`);
          }
          if (!isValidUrl(link.url)) {
            throw new Error(`Please enter a valid retailer URL for ${f.type}.`);
          }
        }

        if (publishNow && !f.isDirect && externalLinks.length === 0) {
          throw new Error(`${f.type} must include at least one retailer link when Sell Direct is unchecked.`);
        }

        const isEbook = f.type === "eBook";
        const hasEbookFile = !!(f.ebookFileUrl || f.ebookS3Key || f.ebookKey || f.ebookUploaded);
        const normalizedPrice = f.isFree ? 0 : f.price === "" ? "" : Number(f.price);

        if ((normalizedPrice !== "" && (!Number.isFinite(normalizedPrice) || normalizedPrice < 0)) || (publishNow && (f.isDirect || f.isFree) && normalizedPrice === "")) {
          throw new Error(`${f.type} requires a valid price before saving.`);
        }

        const base = {
          type: f.type,
          extrainfo: f.extrainfo || "",
          price: normalizedPrice,
          isDirect: !!f.isDirect,
          directShipping: !!f.isDirect,
          externalLinks,
          coverImages: f.coverImages || [],
          coverImageUrl: f.coverImageUrl || "",
          coverKey: f.coverKey || "",
          shipping: normalizeShipping(f.shipping),
        };

        const cleanedVariations = (f.variations || []).map((variation) => {
          const cleanedName = (variation.name || "").trim();
          const cleanedPrice = Number(variation.price);
          const useBaseQuantity = variation.useBaseQuantity !== false;
          const ownQuantity = variation.quantity === "" ? "" : Number(variation.quantity);

          if (!cleanedName) {
            throw new Error(`${f.type} variation names are required.`);
          }

          if (!Number.isFinite(cleanedPrice) || cleanedPrice < 0 || (publishNow && variation.price === "")) {
            throw new Error(`${cleanedName} needs a valid variation price.`);
          }

          if (!useBaseQuantity && ((publishNow && ownQuantity === "") || (ownQuantity !== "" && (!Number.isSafeInteger(ownQuantity) || ownQuantity < 0)))) {
            throw new Error(`${cleanedName} needs a quantity when base inventory is disabled.`);
          }

          const baseVariation = {
            name: cleanedName,
            extrainfo: variation.extrainfo || "",
            price: cleanedPrice,
            useBaseQuantity,
            useBaseInventory: useBaseQuantity,
            images: normalizeVariationImages(variation.images).map((image) => ({
              url: image?.url || "",
              filename: image?.filename || image?.fileName || "",
              authorBucket: image?.authorBucket || "",
              authorKey: image?.authorKey || "",
              masterBucket: image?.masterBucket || "",
              masterKey: image?.masterKey || "",
              key: image?.key || image?.masterKey || "",
            })).filter((image) => image.url),
          };

          if (!useBaseQuantity) baseVariation.quantity = ownQuantity;
          return baseVariation;
        });

        if (publishNow && !hasValidDirectShipping(f)) {
          throw new Error("Weight and package dimensions are required for direct shipping.");
        }

        if (!isEbook && f.isDirect) {
          const normalizedQuantity = f.quantity === "" ? "" : Number(f.quantity);
          if ((publishNow && normalizedQuantity === "") || (normalizedQuantity !== "" && (!Number.isSafeInteger(normalizedQuantity) || normalizedQuantity < 0))) {
            throw new Error(`${f.type} requires a quantity when sold directly.`);
          }
          base.quantity = normalizedQuantity;
        }

        if (cleanedVariations.length) {
          base.variations = cleanedVariations;
        }

        if (isEbook) {
          if (publishNow && f.isDirect && !hasEbookFile) {
            throw new Error("Please upload your eBook file before enabling direct delivery.");
          }
          if (f.ebookFile && !f.ebookS3Key && !f.ebookKey) {
            throw new Error("Please click 'Upload eBook' before saving.");
          }
          return {
            ...base,
            isFree: !!f.isFree,
            ebookS3Key: f.ebookS3Key || f.ebookKey || "",
            ebookKey: f.ebookKey || f.ebookS3Key || "",
            ebookMime: f.ebookMime || "",
            ebookFilesize: f.ebookFilesize || 0,
            ebookFilename: f.ebookFilename || "",
          };
        }

        return base;
      });

      const bookPayload = {
        title: (bookData.title || "").trim(),
        tagline: bookData.tagline || "",
        blurb: bookData.blurb || "",
        extrainfo: bookData.extrainfo || "",
        category,
        genres: cleanGenres,
        subgenres: cleanSubgenres,
        public: publishNow,
        formats: cleanedFormats,
        ...(isSeries && bookData.series?.trim() ? { series: bookData.series.trim() } : {}),
        ...(isSeries && String(bookData.bookNumber || "").trim()
          ? { bookNumber: String(bookData.bookNumber).trim() }
          : {}),
      };

      const payload = { action: "create", sub, firstName, lastName, books: [bookPayload] };
      


      const headers = await authorStorageHeaders();
      requestStarted = true;
      const response = await axios.post(SAVE_URL, payload, { headers });

      if (response.data?.saved !== true) throw new Error("The save could not be confirmed. Please retry.");
      window.dispatchEvent(new Event('bookish:author-books-saved'));
      navigate("/authorportal/mainpage?tab=books");
    } catch (err) {
      console.error("❌ Failed to save book", err);
      if (requestStarted && (!err.response || err.response.status >= 500 || err.response.data?.code === 'BOOK_SAVE_PENDING')) setCanResume(true);
      showError(`Error saving book: ${err?.response?.data?.error || err.message}`);
    } finally { end(); }
  };


  /* ------------------- UI ------------------- */
  return (
    <div className="mx-auto mt-10 w-full max-w-5xl px-2 sm:px-4">
      <div className="bg-bookish-cream/40 border border-bookish-purple/20 p-3 sm:p-8 rounded-2xl shadow-sm">
        <div className="bg-white border border-bookish-purple/20 p-3 sm:p-8 rounded-2xl shadow-xl">
        <h1 className="text-3xl font-serif font-bold">Add New Book</h1>
        <span className="text-sm text-bookish-purple/70">Upload your book details below and select the correct format. 
        As the author, this is YOUR shop and you are responsible for providing accurate representation of your product
        as well as shipping and handling. You can choose to upload your ebooks here and they will be automatically delivered
        to your customer via The Bookish Circle. <br/>If you choose to include an external link, you can add that to any
        format. {/*All Amazon links will be affiliatized*/} </span>
        <p className="mt-3 text-sm">Save For Later keeps a private draft. Publishing requires a title, blurb, genre, cover, and complete purchase details. Images: PNG/JPEG/WebP up to 10 MB. eBooks: EPUB/PDF up to 100 MB.</p>
        <p role="status" aria-live="polite">{busy}</p>
        {feedback && <p ref={feedbackRef} tabIndex={-1} role="alert" className="my-4 text-bookish-purple">{feedback}</p>}
        {canResume && <button type="button" disabled={Boolean(busy)} onClick={resumeSave} className="min-h-[44px] underline">Finish previous book save</button>}
        {feedback && <button type="button" disabled={Boolean(busy)} onClick={() => navigate('/authorportal/mainpage?tab=books')} className="min-h-[44px] underline px-3">Open My Books</button>}
        <fieldset disabled={Boolean(busy)} className="mt-8 space-y-6 min-w-0">
          {/* Title */}
          <div>
            <label htmlFor={"add-book-field-1"} className="block font-semibold">Title:</label>
            <input id={"add-book-field-1"}
              name="title"
              value={displayLabel(bookData.title)}
              onChange={handleInputChange}
              className="w-full border rounded p-2"
            />
          </div>

          {/* Series */}
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={isSeries}
              onChange={(e) => {
                const v = e.target.checked;
                setIsSeries(v);
                if (!v) {
                  // clear when turning OFF so we don’t accidentally send stale values
                  setBookData((b) => ({ ...b, series: "", bookNumber: "" }));
                }
              }}
            />
            <span>This book is part of a series</span>
          </label>

          {isSeries && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor={"add-book-field-2"} className="block text-sm font-medium mb-1">Series Name</label>
                <input id={"add-book-field-2"}
                  type="text"
                  value={displayLabel(bookData.series)}
                  onChange={(e) => setBookData((b) => ({ ...b, series: e.target.value }))}
                  placeholder="e.g., The Magic of Omneth"
                  className="w-full rounded-md border px-3 py-2"
                />
              </div>

              <div>
                <label htmlFor={"add-book-field-3"} className="block text-sm font-medium mb-1">Book Number in Series</label>
                <input id={"add-book-field-3"}
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={bookData.bookNumber}
                  onChange={(e) => setBookData((b) => ({ ...b, bookNumber: e.target.value }))}
                  placeholder="e.g., 1"
                  className="w-full rounded-md border px-3 py-2"
                />
              </div>
            </div>
          )}

          {/* Tagline */}
          <div>
            <label htmlFor={"add-book-field-4"} className="block font-semibold">Tagline:</label>
            <input id={"add-book-field-4"}
              name="tagline"
              value={bookData.tagline}
              onChange={handleInputChange}
              className="w-full border rounded p-2"
            />
          </div>

          {/* Blurb */}
          <div>
            <label htmlFor={"add-book-field-5"} className="block font-semibold">Blurb:</label>
            <textarea id={"add-book-field-5"}
              name="blurb"
              value={bookData.blurb}
              onChange={handleInputChange}
              className="w-full border rounded p-2"
              rows={5}
            />
          </div>
          {/* Extra Info */}
          <div>
            <label htmlFor={"add-book-field-6"} className="block font-semibold">Extra Info (optional):</label>
            <textarea id={"add-book-field-6"}
              name="extrainfo"
              value={bookData.extrainfo}
              onChange={handleInputChange}
              className="w-full border rounded p-2"
              rows={3}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block font-semibold">Category:</label>
            <div className="mt-2 flex flex-col sm:flex-row gap-4">
              {["Fiction", "Non-Fiction"].map((opt) => (
                <label key={opt} className="flex items-center gap-2">
                  <input type="radio" name="book-category" checked={category === opt} onChange={() => onChangeCategory(opt)} />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Main Genre (max 3) */}
          <div>
            <label htmlFor={"add-book-field-7"} className="block font-semibold">Main Genre (max 3):</label>
            <select id={"add-book-field-7"}
              value=""
              onChange={(e) => addMainGenre(e.target.value)}
              className="border p-2 rounded w-full mt-2"
            >
              <option value="">Select a main genre…</option>
              {availableMain.map((g) => (
                <option key={g} value={g} disabled={mainGenres.includes(g) || mainGenres.length >= 3}>
                  {displayLabel(g)}
                </option>
              ))}
            </select>

            <div className="mt-2 flex flex-wrap gap-2">
              {mainGenres.map((g) => {
                const active = selectedMainForSubs === g;
                return (
                  <span
                    key={g}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition ${
                      active
                        ? "bg-bookish-purple text-white border-bookish-purple"
                        : "bg-white text-bookish-purple border-bookish-purple/20"
                    }`}
                  >
                    <button type="button" onClick={() => setSelectedMainForSubs(g)} className="focus:outline-none min-h-[44px]" title="Use this for subgenres">
                      {displayLabel(g)}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeMainGenre(g)}
                      className={`ml-1 rounded-full px-1 ${active ? "hover:bg-white/20" : "hover:bg-bookish-purple/10"}`}
                      aria-label={`Remove ${displayLabel(g)}`}
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Subgenres */}
          <div>
            <label htmlFor="add-book-subgenre" className="block font-semibold">Subgenre (optional):</label>
            {selectedMainForSubs ? (
              <>
                <div className="text-sm text-bookish-purple/70 mt-1">
                  Subgenres for: <span className="font-medium">{displayLabel(selectedMainForSubs)}</span>
                </div>
                {subOptions.length > 0 ? (
                  <select id="add-book-subgenre"
                    value=""
                    onChange={(e) => addSubgenre(e.target.value)}
                    className="border p-2 rounded w-full mt-2"
                  >
                    <option value="">Add a subgenre…</option>
                    {subOptions.map((sg) => (
                      <option key={sg} value={sg} disabled={subgenres.includes(sg)}>
                        {displayLabel(sg)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="mt-2 text-sm text-bookish-purple/60">No predefined subgenres for this main genre.</div>
                )}
              </>
            ) : (
              <div className="mt-2 text-sm text-bookish-purple/60">Select a main genre above to choose subgenres.</div>
            )}

            <div className="mt-2 flex flex-wrap gap-2">
              {subgenres.map((sg) => (
                <button
                  key={sg}
                  type="button"
                  onClick={() => removeSubgenre(sg)}
                  className="px-3 py-1.5 min-h-[44px] rounded-full text-sm border border-bookish-purple/20 bg-white hover:border-bookish-purple/40"
                  title="Remove"
                >
                  {displayLabel(sg)} <span className="ml-1">×</span>
                </button>
              ))}
            </div>
          </div>

          {/* Formats */}
          <hr className="my-6" />
          <h2 className="text-xl font-bold mb-2">Formats</h2>

          <label className="mb-4 flex items-center gap-2 rounded-lg border border-bookish-purple/20 bg-white px-4 py-3">
            <input
              type="checkbox"
              checked={useSameImage}
              onChange={(e) => setUseSameImage(e.target.checked)}
            />
            <span className="font-medium text-bookish-purple">Use same image for all formats</span>
          </label>

          {useSameImage && (
            <div className="mb-4 rounded-xl border border-bookish-purple/20 bg-bookish-cream/40 p-4 shadow-sm">
              <label className="block font-semibold">Shared Cover Image:</label>
              {(() => {
                const sharedPreview = getFormatCoverUrl(
                  formats.find((format) => getFormatCoverUrl(format)) || {}
                );

                return sharedPreview ? (
                  <img src={sharedPreview} alt="Shared cover" className="mb-2 h-48 object-contain" />
                ) : (
                  <div className="mb-2 flex h-48 items-center justify-center bg-gray-100 text-gray-500">
                    No Image
                  </div>
                );
              })()}
              <input aria-label="Shared cover image"
                className="block w-full min-w-0 text-sm"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handleImageUpload(0, e.target.files)}
              />
            </div>
          )}

          {formats.map((format, idx) => {
            const isEbook = format.type === "eBook";
            const hasEbookFile = !!(format.ebookFileUrl || format.ebookS3Key || format.ebookKey || format.ebookUploaded);
            const isDirectDisabled = isEbook && !hasEbookFile;
            const showQuantity = format.isDirect && !isEbook;
            const showShipping = shouldShowShipping(format);
            const shipping = normalizeShipping(format.shipping);
            const hasInvalidDirectShipping = !hasValidDirectShipping(format);
            const purchaseLinks = format.externalLinks || [];
            const showNoPurchaseOptions = !format.isDirect && purchaseLinks.length === 0;
            const canShowVariations = !!format.isDirect;
            const formatVariations = format.variations || [];

            return (
            <div key={idx} className="border p-4 rounded mb-4 bg-bookish-cream/40">
              {!useSameImage && (
                <div className="mb-2">
                  <label className="block">Cover Image:</label>
                  {getFormatCoverUrl(format) ? (
                    <img src={getFormatCoverUrl(format)} alt="Cover" className="h-48 mb-2 object-contain" />
                  ) : (
                    <div className="h-48 bg-gray-100 flex items-center justify-center text-gray-500 mb-2">No Image</div>
                  )}
                  <input className="block w-full min-w-0 text-sm" aria-label={format.type + " cover image"} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => handleImageUpload(idx, e.target.files)} />
                </div>
              )}

              {useSameImage && (
                <div className="mb-3 rounded-lg border border-bookish-purple/10 bg-white px-3 py-2 text-sm text-bookish-purple/70">
                  Using shared cover image for this format.
                </div>
              )}

              <label htmlFor={"add-book-field-8-" + idx}>Type:</label>
              <select id={"add-book-field-8-" + idx}
                value={format.type}
                onChange={(e) => handleFormatChange(idx, "type", e.target.value)}
                className="w-full border p-2 mb-2"
              >
                {formatOptions.map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>

              {isEbook && (
                <p className="mb-3 text-sm text-bookish-purple/70">Digital format — no shipping required</p>
              )}

              {/* eBook block */}
              {isEbook && (
                <div className="mt-3 rounded-lg border border-bookish-purple/20 bg-white p-3">
                  <label htmlFor={"add-book-field-9-" + idx} className="block text-sm font-medium text-bookish-royal mb-2">
                    eBook File (.epub or .pdf)
                  </label>

                  <input id={"add-book-field-9-" + idx}
                    type="file"
                    accept=".epub,.pdf,application/epub+zip,application/pdf"
                    onChange={(e) => handleEbookFileChange(idx, e.target.files?.[0] || null)}
                    className="block w-full text-sm file:mr-3 file:rounded-xl file:border-0 file:bg-bookish-purple file:px-4 file:py-2 file:text-white hover:file:brightness-110"
                  />

                  {format.ebookFilename ? (
                    <div className="mt-2 text-sm text-bookish-royal/80">
                      Selected: <span className="font-medium">{format.ebookFilename}</span>
                      <span className="ml-2 text-bookish-royal/60">
                        ({formatBytes(format.ebookFilesize)})
                      </span>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-bookish-royal/60">
                      Accepted: .epub or .pdf • Maximum 100 MB
                    </p>
                  )}

                  <div className="mt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => uploadEbookForFormat(idx)}
                      disabled={!format.ebookFile || ebookUploadingIdx === idx || !hasTitle}
                      className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white ${
                        !format.ebookFile || ebookUploadingIdx === idx || !hasTitle
                          ? "bg-bookish-purple/60 cursor-not-allowed"
                          : "bg-bookish-purple hover:brightness-110"
                      }`}
                      title={!hasTitle ? "Enter a Title first" : ""}
                    >
                      <Upload className="h-4 w-4" />
                      {ebookUploadingIdx === idx ? "Uploading…" : "Upload eBook"}
                    </button>

                    {!hasTitle && (
                      <p className="mt-2 text-xs text-amber-700">
                        Add a <span className="font-medium">Title</span> above to enable uploading.
                      </p>
                    )}


                    {format.ebookUploaded || format.ebookS3Key ? (
                      <span className="inline-flex items-center gap-1 text-green-700 text-sm">
                        <CheckCircle2 className="h-4 w-4" />
                        Uploaded
                      </span>
                    ) : format.ebookFile ? (
                      <span className="inline-flex items-center gap-1 text-amber-700 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        Not uploaded yet
                      </span>
                    ) : null}

                    {format.ebookFilename && (
                      <button
                        type="button"
                        onClick={() => clearEbookFile(idx)}
                        className="text-bookish-purple hover:underline text-sm ml-auto"
                      >
                        Remove file
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-3">
                <label htmlFor={"add-book-price-" + idx} className="block font-semibold">Price:</label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-2">
                  <input id={"add-book-price-" + idx}
                    type="text"
                    inputMode="decimal"
                    value={format.isFree ? "0.00" : (format.price ?? "")}
                    onChange={(e) => {
                      let v = e.target.value.replace(/[^\d.]/g, "");
                      const parts = v.split(".");
                      if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
                      if (parts[1]?.length > 2) v = parts[0] + "." + parts[1].slice(0, 2);
                      handleFormatChange(idx, "price", v);
                    }}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (!format.isFree && v !== "") {
                        const n = Number(v);
                        if (Number.isFinite(n)) handleFormatChange(idx, "price", n.toFixed(2));
                      }
                    }}
                    disabled={format.isFree}
                    required={!!format.isDirect || !!format.isFree}
                    className={`w-full border p-2 ${format.isFree ? "bg-gray-100 text-gray-500" : ""}`}
                    placeholder={format.isFree ? "Free" : "0.00"}
                    title="Enter a price like 4.99"
                  />

                  {isEbook && (
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!format.isFree}
                        onChange={(e) => handleFormatChange(idx, "isFree", e.target.checked)}
                      />
                      Free
                    </label>
                  )}
                </div>
              </div>
              <div className="mt-3">
                <label htmlFor={"add-book-field-10-" + idx} className="block font-semibold">
                  Extra Information (optional):
                </label>
                <textarea id={"add-book-field-10-" + idx}
                  value={format.extrainfo || ""}
                  onChange={(e) =>
                    handleFormatChange(idx, "extrainfo", e.target.value)
                  }
                  className="w-full border rounded p-2"
                  rows={3}
                  placeholder="Signed copy, includes bookmarks, goodies, etc."
                />
              </div>
              <div className="mt-3 rounded-lg border border-bookish-purple/20 bg-white p-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={!!format.isDirect}
                    onChange={(e) => handleFormatChange(idx, "isDirect", e.target.checked)}
                    disabled={isDirectDisabled}
                    className="mt-1"
                  />
                  <span>
                    <span className="block font-semibold">
                      {isEbook
                        ? "I have uploaded this eBook and confirm it will be delivered directly to readers after purchase."
                        : "I will sell and fulfill this format directly (shipping, delivery, etc.)"}
                    </span>
                    <span className="mt-1 block text-sm text-bookish-purple/70">
                      {isEbook
                        ? "This file will be automatically delivered to the reader after purchase."
                        : "You are responsible for shipping and fulfillment for this format."}
                    </span>
                  </span>
                </label>
                {isDirectDisabled && (
                  <p className="mt-2 text-sm text-amber-700">
                    Please upload your eBook file before enabling direct delivery.
                  </p>
                )}
              </div>

              {showQuantity && (
                <>
                  <label htmlFor={"add-book-field-11-" + idx} className="mt-3 block font-semibold">Quantity:</label>
                  <input id={"add-book-field-11-" + idx}
                    type="number"
                    min="0"
                    value={format.quantity}
                    onChange={(e) => handleFormatChange(idx, "quantity", e.target.value)}
                    className="w-full border p-2"
                  />
                </>
              )}

              {canShowVariations && (
                <div className="mt-4 rounded-lg border border-bookish-purple/20 bg-white p-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-semibold">Variations</h3>
                    <button
                      type="button"
                      onClick={() => toggleVariationsSection(idx)}
                      className="text-sm text-bookish-purple hover:underline"
                    >
                      {format.showVariations ? "Hide Variations" : "Add Variations"}
                    </button>
                  </div>

                  {format.showVariations && (
                    <div className="mt-3 space-y-3">
                      {formatVariations.map((variation, variationIdx) => (
                        <div key={`${idx}-variation-${variationIdx}`} className="rounded-lg border border-bookish-purple/10 p-3">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div>
                              <label htmlFor={"add-book-field-12-" + idx + "-" + variationIdx} className="block text-sm font-semibold">Name</label>
                              <input id={"add-book-field-12-" + idx + "-" + variationIdx}
                                type="text"
                                value={displayLabel(variation.name || "")}
                                onChange={(e) => handleVariationChange(idx, variationIdx, "name", e.target.value)}
                                className="w-full rounded border p-2"
                                placeholder="Sprayed Edges"
                              />
                            </div>
                            <div>
                              <label htmlFor={"add-book-field-13-" + idx + "-" + variationIdx} className="block text-sm font-semibold">Price</label>
                              <input id={"add-book-field-13-" + idx + "-" + variationIdx}
                                type="number"
                                min="0"
                                step="0.01"
                                value={variation.price ?? ""}
                                onChange={(e) => handleVariationChange(idx, variationIdx, "price", e.target.value)}
                                className="w-full rounded border p-2"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                          <label htmlFor={"add-book-field-14-" + idx + "-" + variationIdx} className="block text-sm font-semibold">
                            Extra Information
                          </label>
                          <textarea id={"add-book-field-14-" + idx + "-" + variationIdx}
                            value={variation.extrainfo || ""}
                            onChange={(e) =>
                              handleVariationChange(
                                idx,
                                variationIdx,
                                "extrainfo",
                                e.target.value
                              )
                            }
                            className="w-full rounded border p-2"
                            rows={3}
                            placeholder="Sprayed gold edges, signed, no goodies included."
                          />
                        </div>
                          <label className="mt-3 flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={variation.useBaseQuantity !== false}
                              onChange={(e) => handleVariationChange(idx, variationIdx, "useBaseQuantity", e.target.checked)}
                            />
                            Use same inventory as base format
                          </label>

                          {variation.useBaseQuantity === false && (
                            <div className="mt-2">
                              <label htmlFor={"add-book-field-15-" + idx + "-" + variationIdx} className="block text-sm font-semibold">Quantity</label>
                              <input id={"add-book-field-15-" + idx + "-" + variationIdx}
                                type="number"
                                min="0"
                                value={variation.quantity ?? ""}
                                onChange={(e) => handleVariationChange(idx, variationIdx, "quantity", e.target.value)}
                                className="w-full rounded border p-2"
                              />
                            </div>
                          )}

                          <div className="mt-3">
                            <label htmlFor={"add-book-field-16-" + idx + "-" + variationIdx} className="block text-sm font-semibold">
                              Variation Images (max {MAX_VARIATION_IMAGES})
                            </label>
                            <input id={"add-book-field-16-" + idx + "-" + variationIdx}
                              className="block w-full min-w-0 text-sm"
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              onChange={(e) => handleVariationImageUpload(idx, variationIdx, e.target.files)}
                              disabled={(variation.images || []).length >= MAX_VARIATION_IMAGES}
                            />
                            {(variation.images || []).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {variation.images.map((image, imageIdx) => (
                                  <div key={`${idx}-${variationIdx}-image-${imageIdx}`} className="relative">
                                    <img src={image.url} alt={displayLabel(variation.name || "Variation")} className="h-16 w-16 rounded object-cover" />
                                    <button
                                      type="button"
                                      onClick={() => removeVariationImage(idx, variationIdx, imageIdx)}
                                      className="absolute -right-2 -top-2 rounded-full bg-white px-1 text-xs text-red-500 shadow"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() => removeVariation(idx, variationIdx)}
                            className="mt-3 text-sm text-red-500 hover:underline"
                          >
                            Remove Variation
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() => addVariation(idx)}
                        className="inline-flex items-center gap-1 rounded-md border border-bookish-purple/20 px-3 py-2 text-sm text-bookish-purple hover:bg-bookish-purple/5"
                      >
                        <Plus className="h-4 w-4" /> Add Variation
                      </button>
                    </div>
                  )}
                </div>
              )}

              {showShipping && (
                <div className="mt-4 rounded-lg border border-bookish-purple/20 bg-white p-4">
                  <h3 className="font-semibold">Shipping</h3>
                  <div className="mt-4 space-y-2">
                    <div>
                      <label htmlFor={"add-book-field-17-" + idx} className="block text-sm font-semibold">Weight (lbs)</label>
                      <input id={"add-book-field-17-" + idx}
                        type="number"
                        min="0"
                        step="0.01"
                        required={showShipping}
                        value={shipping.weight}
                        onChange={(e) => handleFormatChange(idx, "shipping.weight", e.target.value)}
                        placeholder="e.g. 1.2"
                        className="w-full border p-2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold">Dimensions (inches)</label>
                      <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={shipping.length}
                          onChange={(e) => handleFormatChange(idx, "shipping.length", e.target.value)}
                          aria-label={format.type + " package length in inches"} placeholder="Length"
                          className="w-full border p-2"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={shipping.width}
                          onChange={(e) => handleFormatChange(idx, "shipping.width", e.target.value)}
                          aria-label={format.type + " package width in inches"} placeholder="Width"
                          className="w-full border p-2"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={shipping.height}
                          onChange={(e) => handleFormatChange(idx, "shipping.height", e.target.value)}
                          aria-label={format.type + " package height in inches"} placeholder="Height"
                          className="w-full border p-2"
                        />
                      </div>
                    </div>

                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!shipping.freeShipping}
                        onChange={(e) => handleFormatChange(idx, "shipping.freeShipping", e.target.checked)}
                        className="mt-1"
                      />
                      <span>
                        <span className="block font-semibold">Free Shipping</span>
                        <span className="block text-bookish-purple/70">
                          Offer this item with free shipping to the reader. A shipping address will still be collected and you can still purchase a shipping label.
                        </span>
                      </span>
                    </label>

                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!shipping.mediaMailEligible}
                        onChange={(e) => handleFormatChange(idx, "shipping.mediaMailEligible", e.target.checked)}
                        disabled={!!shipping.hasExtras}
                        className="mt-1"
                      />
                      <span>Eligible for USPS Media Mail (books only)</span>
                    </label>

                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!shipping.hasExtras}
                        onChange={(e) => handleFormatChange(idx, "shipping.hasExtras", e.target.checked)}
                        className="mt-1"
                      />
                      <span>Includes extras (stickers, inserts, swag, etc.)</span>
                    </label>

                    {shipping.mediaMailEligible && shipping.hasExtras && (
                      <p className="text-xs text-amber-700">
                        Media Mail cannot include extras. This option will be disabled at checkout.
                      </p>
                    )}
                    {hasInvalidDirectShipping && (
                      <p className="text-xs text-red-600">
                        Weight and package dimensions are required for direct shipping.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-4 rounded-lg border border-bookish-purple/20 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <label className="block font-semibold">Retailer Links</label>
                    <p className="text-sm text-bookish-purple/70">
                      Add optional fallback retailers when selling direct, or required retailer links for retail-only formats.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => addExternalLink(idx)}
                    className="inline-flex items-center gap-1 rounded-md border border-bookish-purple/20 px-3 py-2 text-sm text-bookish-purple hover:bg-bookish-purple/5"
                  >
                    <Plus className="h-4 w-4" /> Add Retailer
                  </button>
                </div>

                <div className="mt-3 space-y-3">
                  {purchaseLinks.map((link, linkIdx) => {
                    const showError = (!format.isDirect || link.label || link.url) && (!link.label || !link.url);
                    const showUrlError = !!link.url && !isValidUrl(link.url);
                    return (
                      <div key={`${idx}-${linkIdx}`} className="grid grid-cols-1 gap-3 rounded-lg border border-bookish-purple/10 p-3 md:grid-cols-[1fr_1fr_auto]">
                        <div>
                          <label htmlFor={"add-book-field-18-" + idx + "-" + linkIdx} className="block text-sm font-semibold">Label</label>
                          <input id={"add-book-field-18-" + idx + "-" + linkIdx}
                            type="text"
                            placeholder="Amazon"
                            value={link.label}
                            onChange={(e) => handleExternalLinkChange(idx, linkIdx, "label", e.target.value)}
                            className={`w-full rounded border p-2 ${showError && !link.label ? "border-red-400" : ""}`}
                          />
                        </div>

                        <div>
                          <label htmlFor={"add-book-field-19-" + idx + "-" + linkIdx} className="block text-sm font-semibold">URL</label>
                          <input id={"add-book-field-19-" + idx + "-" + linkIdx}
                            type="url"
                            placeholder="https://example.com/book"
                            value={link.url}
                            onChange={(e) => handleExternalLinkChange(idx, linkIdx, "url", e.target.value)}
                            className={`w-full rounded border p-2 ${showError && !link.url ? "border-red-400" : showUrlError ? "border-red-400" : ""}`}
                          />
                        </div>

                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeExternalLink(idx, linkIdx)}
                            className="text-sm text-red-500 hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {showNoPurchaseOptions && (
                  <p className="mt-3 text-sm text-amber-700">No purchase options available</p>
                )}
              </div>

              {formats.length > 1 && (
                <button type="button" onClick={() => removeFormat(idx)} className="text-red-500 mt-2">
                  Remove Format
                </button>
              )}
            </div>
          );
          })}

          <button disabled={formats.length >= 4} onClick={addFormat} className="text-sm text-bookish-purpleAccent">
            <Plus className="inline h-4 w-4" /> Add Another Format
          </button>

          {/* Save / Publish */}
          {hasInvalidDirectShipping && <p className="text-sm">Complete the physical format’s weight and dimensions to publish, or save a private draft now.</p>}
          <div className="flex flex-wrap gap-4 mt-6">
            <button
              onClick={(e) => {
                e.preventDefault();
                handleSave(false);
              }}
              className="bg-bookish-purple text-white px-4 py-2 rounded-md hover:bg-bookish-purpleAccent"
            >
              Save For Later
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                handleSave(true); // forces public: true in payload
              }}
              disabled={hasInvalidDirectShipping}
              className={`text-white px-4 py-2 rounded-md ${
                hasInvalidDirectShipping
                  ? "bg-green-600/60 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              Publish Now
            </button>
          </div>
        </fieldset>
      </div>
    </div>
  </div>
  );
};

export default AddBook;
